/**
 * Wraps the app with MSAL provider and wires SSO token getter + redirect handling.
 * When user returns from Azure AD redirect, acquires token, fetches /users/me, and sets auth store.
 * We run handleRedirectPromise() once before rendering the provider so we can log token-exchange errors (e.g. 400).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { msalConfig, apiRequest, loginRequest, callbackPath } from '../config/authConfig';
import { msalInstance } from '../config/msalInstance';
import { setSsoTokenGetter } from '../services/ssoTokenProvider';
import { useAuthStore } from '../store/authStore';
import { useContextStore } from '../store/contextStore';
import { apiClient } from '../services/api';
import { ROUTES } from '../utils/routes';
import type { UserDto } from '../types/auth';

/**
 * Shared with non-React callers (`contextStore` reads the organizations claim off the signed-in
 * account), so the instance is owned by its own module rather than created here — a second
 * instance would keep a separate view of the token cache.
 */
const pca = msalInstance;

/** True when path is the configured callback path and URL has code/state (normal B2C redirect). */
function isCallbackPageWithRedirect(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const want = callbackPath.replace(/\/$/, '') || '/';
  if (path !== want) return false;
  return hasRedirectParamsInUrl();
}

/**
 * True when URL has OAuth code/state on any path. MSAL SPAs use
 * response_mode=fragment, so the B2C response arrives in the hash
 * (#state=...&code=...), not the query string — check both.
 */
function hasRedirectParamsInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const q = window.location.search;
  const h = window.location.hash;
  return q.includes('code=') || q.includes('state=') || h.includes('code=') || h.includes('state=');
}

function SsoAuthHandler({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const { setAuth, isAuthenticated } = useAuthStore();
  const hasSetTokenGetter = useRef(false);
  const hasHandledRedirect = useRef(false);

  // Register SSO token getter so api client can get current access token
  useEffect(() => {
    if (hasSetTokenGetter.current || !instance || accounts.length === 0) return;
    hasSetTokenGetter.current = true;
    if (import.meta.env.DEV) console.log('[SsoAuthHandler] SSO token getter registered (accounts.length > 0)');
    setSsoTokenGetter(async () => {
      try {
        const account = accounts[0];
        const result = await instance.acquireTokenSilent({
          ...apiRequest,
          account,
        });
        if (import.meta.env.DEV) {
          console.debug('[sso] acquireTokenSilent OK for scopes', apiRequest.scopes, {
            scopesGranted: result.scopes,
            hasAccessToken: !!result.accessToken,
            expiresOn: result.expiresOn,
          });
        }
        return result.accessToken;
      } catch (err) {
        // This is the real reason API calls 401: silent acquisition of the API-scope
        // access token failed. Log the full MSAL error (errorCode/subError explain why:
        // e.g. interaction_required, invalid_grant, AADB2C90205, scope/consent issues).
        const e = err as { errorCode?: string; subError?: string; errorMessage?: string; message?: string };
        console.error('[sso] acquireTokenSilent FAILED for scopes', apiRequest.scopes, {
          errorCode: e?.errorCode,
          subError: e?.subError,
          errorMessage: e?.errorMessage ?? e?.message,
          fullError: err,
        });
        return null;
      }
    });
    return () => {
      setSsoTokenGetter(null);
      hasSetTokenGetter.current = false;
    };
  }, [instance, accounts]);

  // When user returns from Azure AD redirect: we have account but may not have set our auth store yet
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[SsoAuthHandler] effect run', {
        inProgress,
        accountsCount: accounts.length,
        hasHandledRedirect: hasHandledRedirect.current,
        isAuthenticated,
        path: window.location.pathname,
      });
    }
    if (inProgress !== InteractionStatus.None) {
      if (import.meta.env.DEV) console.log('[SsoAuthHandler] skip: inProgress !== None');
      return;
    }
    if (accounts.length === 0) {
      if (import.meta.env.DEV) console.log('[SsoAuthHandler] skip: no accounts');
      return;
    }
    if (hasHandledRedirect.current) {
      if (import.meta.env.DEV) console.log('[SsoAuthHandler] skip: already handled redirect');
      return;
    }
    if (isAuthenticated) {
      if (import.meta.env.DEV) console.log('[SsoAuthHandler] skip: already authenticated');
      return;
    }

    hasHandledRedirect.current = true;
    if (import.meta.env.DEV) console.log('[SsoAuthHandler] starting post-login flow: token → ensure user+tenant → /users/me → setAuth → navigate(/)');
    (async () => {
      const account = accounts[0];
      let token: string;

      try {
        // Step 1: get a token (prefer API scope; fallback to ID token so user can still reach home)
        if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 1: acquiring token for API scope...');
        try {
          const tokenResult = await instance.acquireTokenSilent({
            ...apiRequest,
            account,
          });
          if (!tokenResult.accessToken) throw new Error('No access token in result');
          token = tokenResult.accessToken;
          if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 1 done: API token acquired');
        } catch (tokenErr) {
          if (import.meta.env.DEV) console.warn('[SsoAuthHandler] step 1 API token failed, using ID token fallback:', (tokenErr as Error)?.message ?? tokenErr);
          const idResult = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
          });
          token = idResult.idToken ?? idResult.accessToken ?? '';
          if (!token) {
            if (import.meta.env.DEV) console.error('[SsoAuthHandler] step 1 failed: no token from API or login request');
            hasHandledRedirect.current = false;
            return;
          }
          if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 1 fallback: using ID token');
        }

        // Step 2: Ensure the local user row exists (membership comes from the IdP directory, SEC-C04)
        if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 2: POST /users/me/ensure (JIT user; directory membership)...');
        try {
          await apiClient.post(
            '/users/me/ensure',
            {},
            {
              headers: { Authorization: `Bearer ${token}` },
              skipAuthRedirect: true, // do not redirect to login on 401 during SSO post-login
            } as Parameters<typeof apiClient.post>[2] & { skipAuthRedirect?: boolean }
          );
          if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 2 done: ensure user+tenant success');
        } catch (ensureErr: any) {
          const status = ensureErr?.response?.status;
          const data = ensureErr?.response?.data;
          if (import.meta.env.DEV) console.warn('[SsoAuthHandler] step 2 ensure failed (continuing):', status, data ?? (ensureErr?.message ?? ensureErr));
        }

        // Step 3: get user from API or build from account
        if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 3: calling GET /users/me...');
        let user: UserDto;
        try {
          const { data } = await apiClient.get<UserDto>('/users/me', {
            headers: { Authorization: `Bearer ${token}` },
            skipAuthRedirect: true, // do not clear auth / redirect on 401 during SSO post-login
          } as Parameters<typeof apiClient.get>[1] & { skipAuthRedirect?: boolean });
          user = data;
          if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 3 done: /users/me success', { userId: user?.id, email: user?.email });
        } catch (apiErr) {
          const e = apiErr as { response?: { status?: number } };
          if (import.meta.env.DEV) console.warn('[SsoAuthHandler] step 3 /users/me failed:', e?.response?.status, '(building user from account)');
          const nameParts = (account.name ?? account.username ?? '').trim().split(/\s+/, 2);
          user = {
            id: account.localAccountId ?? account.homeAccountId ?? crypto.randomUUID(),
            email: account.username ?? '',
            firstName: nameParts[0] ?? '',
            lastName: nameParts[1] ?? '',
            fullName: account.name ?? account.username ?? '',
            isActive: true,
            emailVerified: true,
            roles: [],
          };
        }

        if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 4: setAuth(...)');
        setAuth(token, user);

        // Step 4a: Load tenants into context store (API headers and project list)
        if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 4a: loading tenants into context store...');
        try {
          await useContextStore.getState().loadTenants();
          if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 4a done: tenants loaded');
        } catch (tenantErr) {
          if (import.meta.env.DEV) console.warn('[SsoAuthHandler] step 4a tenant load failed (continuing):', tenantErr);
        }

        // Step 5: land the user where they actually asked to be.
        //
        // WHY this is conditional. `msalConfig` sets `navigateToLoginRequestUrl: true`, so MSAL has
        // already restored the URL the user originally opened — a deep link like
        // `/projects/123?tab=runs` included. Navigating to HOME unconditionally threw that away on
        // every single sign-in, so a shared link, a bookmark, or a refresh after the token expired
        // all silently became the dashboard. The user then had to find the page again by hand, and
        // it read as "the link doesn't work" rather than as a redirect.
        //
        // HOME is still right when there is no destination to keep: the bare origin, or the OAuth
        // callback path itself, which is not a page anyone should be left looking at.
        const landing = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const onCallbackPath =
          window.location.pathname.replace(/\/$/, '') === callbackPath.replace(/\/$/, '');
        const hasDestination =
          !onCallbackPath && window.location.pathname.replace(/\/$/, '') !== '';

        if (hasDestination) {
          if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 5: keeping requested route →', landing);
        } else {
          if (import.meta.env.DEV) console.log('[SsoAuthHandler] step 5: navigate(ROUTES.HOME) →', ROUTES.HOME);
          navigate(ROUTES.HOME, { replace: true });
        }
        if (import.meta.env.DEV) console.log('[SsoAuthHandler] post-login flow complete');
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('[SsoAuthHandler] post-login flow failed', err);
          const e = err as { response?: { status?: number; data?: unknown }; message?: string };
          if (e.response) console.error('[SsoAuthHandler] API response', e.response.status, e.response.data);
        }
        hasHandledRedirect.current = false;
      }
    })();
  }, [inProgress, accounts, instance, setAuth, navigate, isAuthenticated]);

  return <>{children}</>;
}

interface MsalProviderWithAuthProps {
  children: React.ReactNode;
}

export function MsalProviderWithAuth({ children }: MsalProviderWithAuthProps) {
  const [initialized, setInitialized] = useState(false);
  const [redirectHandled, setRedirectHandled] = useState(false);
  const redirectPromiseStarted = useRef(false);

  useEffect(() => {
    pca.initialize().then(() => setInitialized(true)).catch(() => setInitialized(true));
  }, []);

  // When URL has code/state (from B2C redirect), run handleRedirectPromise once so we can complete login and log errors.
  // We run for any path so that if B2C redirect URI is wrong (e.g. /login instead of /signin-oidc), we still try the token exchange.
  // Ref guards against Strict Mode double-mount running the exchange twice (second would 400).
  useEffect(() => {
    if (!initialized || redirectHandled) return;
    if (!hasRedirectParamsInUrl()) {
      if (import.meta.env.DEV) console.log('[MsalProviderWithAuth] no code/state in URL, ready to render. path=', typeof window !== 'undefined' ? window.location.pathname : '');
      setRedirectHandled(true);
      return;
    }
    if (redirectPromiseStarted.current) {
      if (import.meta.env.DEV) console.log('[MsalProviderWithAuth] handleRedirectPromise already started (waiting for result)');
      return;
    }
    redirectPromiseStarted.current = true;
    if (import.meta.env.DEV) {
      const onCallbackPath = isCallbackPageWithRedirect();
      console.log('[MsalProviderWithAuth] URL has code/state, running handleRedirectPromise. path=', typeof window !== 'undefined' ? window.location.pathname : '', 'onCallbackPath=', onCallbackPath, 'redirectUri=', msalConfig.auth.redirectUri);
    }
    pca
      .handleRedirectPromise()
      .then((result) => {
        if (import.meta.env.DEV) {
          console.log('[MsalProviderWithAuth] handleRedirectPromise result:', result ? { account: result.account?.username } : 'null');
        }
        setRedirectHandled(true);
      })
      .catch((err: unknown) => {
        const e = err as { message?: string; errorCode?: string; errorMessage?: string; response?: { status?: number; body?: unknown }; body?: unknown; sub?: string };
        if (import.meta.env.DEV) {
          console.error('[MsalProviderWithAuth] handleRedirectPromise failed. Ensure redirect URI in Azure B2C app registration matches exactly:', msalConfig.auth.redirectUri);
          console.error('[MsalProviderWithAuth] error:', e?.message ?? e?.errorMessage ?? String(err));
          console.error('[MsalProviderWithAuth] error details:', {
            errorCode: e?.errorCode,
            responseStatus: e?.response?.status,
            responseBody: e?.response?.body ?? e?.body,
            fullError: err,
          });
        }
        setRedirectHandled(true);
      });
  }, [initialized, redirectHandled]);

  if (!initialized || !redirectHandled) {
    if (import.meta.env.DEV && initialized) console.log('[MsalProviderWithAuth] waiting for redirectHandled...');
    return null;
  }

  if (import.meta.env.DEV) console.log('[MsalProviderWithAuth] rendering MsalProvider + SsoAuthHandler');
  return (
    <MsalProvider instance={pca}>
      <SsoAuthHandler>{children}</SsoAuthHandler>
    </MsalProvider>
  );
}
